import { ButtonText } from "components/Atoms/Button";
import TooltipBase from "components/Tooltip/TooltipBase";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { DateRange, DayPicker, TZDate } from "react-day-picker";
import "react-day-picker/style.css";
import { Input } from "semantic-ui-react";
import getConfiguredLocale from "./getConfiguredLocale";
import { useSemanticUiInputRef } from "utilities/hookutils";
import { useValidate } from "formvalidation/hooks";

interface CommonDatePickerProps {
  dontUpdateOnInputChange?: boolean;
  focusOnMount?: boolean;
  value: moment.Moment;
  onChange: (value: moment.Moment, fromCalendar: boolean) => void;
  minDate: moment.Moment;
  maxDate: moment.Moment;
  onEsc?: () => void;
  onClose?: () => void;
  onEnter?: () => void;
}

export type FilterDatePickerProps = CommonDatePickerProps;
export interface FormDatePickerProps extends CommonDatePickerProps {
  error?: boolean;
  validatorKey: string;
}

type DatePickerBaseMode = "single" | "rangeStart" | "rangeEnd";

interface DatePickerBaseProps extends CommonDatePickerProps {
  mode: DatePickerBaseMode;
  innerRef: React.LegacyRef<Input>;
  error: boolean;
  rangeValue?: moment.Moment;
}

interface FilterDatePickerBaseProps extends CommonDatePickerProps {
  mode: DatePickerBaseMode;
  rangeValue?: moment.Moment;
}

interface FormDatePickerBaseProps extends CommonDatePickerProps {
  mode: DatePickerBaseMode;
  error?: boolean;
  rangeValue?: moment.Moment;
  validatorKey: string;
}

export const FilterDatePickerBase = (props: FilterDatePickerBaseProps) => {
  const semanticRef = useSemanticUiInputRef();
  const { onChange, ...rest } = props;

  const onValueChange = (value: moment.Moment, fromCalendar: boolean) => {
    if (value.isBefore(props.minDate)) {
      onChange(props.minDate, fromCalendar);
    } else if (value.isAfter(props.maxDate)) {
      onChange(props.maxDate, fromCalendar);
    } else {
      onChange(value, fromCalendar);
    }
  }

  return <DatePickerBase
    innerRef={semanticRef.refSetter}
    error={false}
    {...rest}
    onChange={onValueChange}
  />
}

export const FormDatePickerBase = (props: FormDatePickerBaseProps) => {
  const semanticRef = useSemanticUiInputRef();

  const validate = async (dateString: string) => {
    const value = moment.utc(dateString, "L", moment.locale());
    return value.isSameOrAfter(props.minDate) && value.isSameOrBefore(props.maxDate);
  }

  const validation = useValidate({
    key: props.validatorKey,
    title: props.validatorKey,
    value: props.value.format("L"),
    validate: validate,
    ref: semanticRef.ref,
  });

  useEffect(() => {
    if (props.focusOnMount) {
      semanticRef.ref.current.focus();
    }
  }, []);

  const error = props.error !== undefined ? props.error : validation.error.hasError;

  if (error) {
    console.warn(`Overwriting validation error for field date picker ${props.validatorKey}`);
  }

  return <DatePickerBase
    innerRef={semanticRef.refSetter}
    error={error}
    {...props}
  />
}

const DatePickerBase = (props: DatePickerBaseProps) => {
  const {
    dontUpdateOnInputChange = false,
    focusOnMount = false,
    error,
    value,
    onEsc,
    onEnter,
    onClose,

    minDate,
    maxDate,
    mode,
    innerRef,
    rangeValue = moment.utc(),
  } = props;

  const onChange = (v: moment.Moment, fromCalendar: boolean) => {
    const newValue = value.clone();

    // DatePicker only sets DD-MM-YYYY, not the time. We want to preserve the time
    newValue.year(v.year());
    newValue.month(v.month());
    newValue.date(v.date());

    props.onChange(newValue, fromCalendar);
  }

  const isDayDisabled = (date: Date): boolean => {
    const m = moment.utc(date);
    if (m.isAfter(maxDate) || m.isBefore(minDate)) {
      return true;
    }

    return false;
  }

  const valueTz = momentToTzDate(value);
  const rangeValueTz = momentToTzDate(rangeValue);
  const [isCalendarOpen, setIsCalendarOpen] = useState(focusOnMount);
  const [monthInCalendar, setMonthInCaledar] = useState<TZDate>(valueTz);

  useEffect(() => {
    setMonthInCaledar(valueTz);
  }, [valueTz.toISOString()]);

  const onChangeInput = (date: moment.Moment) => {
    onChange(date, false);
  }

  const onClickInput = () => {
    setIsCalendarOpen(true);
  }

  const handleEnter = (_: React.KeyboardEvent) => {
    // Close calendar if it's open on enter, instead of submitting
    if (!onEnter && isCalendarOpen) {
      setIsCalendarOpen(false);
      return;
    }

    onEnter?.();
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onEsc?.();
    } else if (e.key === "Enter") {
      handleEnter(e);
    } else if (e.key === "Tab") {
      if (isCalendarOpen) {
        setIsCalendarOpen(false);
      }
    }
  }

  const onSelect = (newVal: Date | DateRange, triggerDate: Date) => {
    setIsCalendarOpen(false);
    if (mode === "single") {
      onChange(moment.utc(newVal as Date), true);
    } else if (mode === "rangeStart") {
      onChange(moment.utc(triggerDate), true);
    } else if (mode === "rangeEnd") {
      onChange(moment.utc(triggerDate), true);
    }
  }

  return <TooltipBase
    startOpen={focusOnMount}
    position="bottom center"
    onOpen={() => setIsCalendarOpen(true)}
    onClose={() => { setIsCalendarOpen(false); onClose?.() }}
    tooltipStyle={{
      width: 256,
      minWidth: 256,
      maxWidth: 256,
    }}
    open={isCalendarOpen}
    trigger={<DatePickerInput
      updateOnInputChange={!dontUpdateOnInputChange}
      focusOnMount={focusOnMount}
      value={value}
      error={error}
      minDate={minDate}
      maxDate={maxDate}
      innerRef={innerRef}
      onKeyDown={onKeyDown}
      onChange={onChangeInput}
      onClick={onClickInput}
    />}
  >
    <div className="padding-larger">
      {mode === "single" && <DayPicker
        required
        startMonth={momentToTzDate(minDate)}
        endMonth={momentToTzDate(maxDate)}
        disabled={isDayDisabled}
        captionLayout="dropdown"
        timeZone={timeZone}
        locale={getConfiguredLocale(site.culture.uiLocale)}
        selected={valueTz}
        onSelect={onSelect}
        month={monthInCalendar}
        onMonthChange={m => setMonthInCaledar(new TZDate(m, timeZone))}
        fixedWeeks
        showOutsideDays
        formatters={{
          formatWeekdayName: (date) => serverReference.abbreviatedDayLetters[date.getDay()]
        }}
        mode="single"
      />}
      {mode === "rangeStart" && <DayPicker
        required
        startMonth={momentToTzDate(minDate)}
        endMonth={momentToTzDate(maxDate)}
        disabled={isDayDisabled}
        captionLayout="dropdown"
        timeZone={timeZone}
        locale={getConfiguredLocale(site.culture.uiLocale)}
        selected={{
          from: valueTz,
          to: rangeValueTz,
        }}
        onSelect={onSelect}
        month={monthInCalendar}
        onMonthChange={m => setMonthInCaledar(new TZDate(m, timeZone))}
        fixedWeeks
        showOutsideDays
        formatters={{
          formatWeekdayName: (date) => serverReference.abbreviatedDayLetters[date.getDay()]
        }}
        mode="range"
      />}
      {mode === "rangeEnd" && <DayPicker
        required
        startMonth={momentToTzDate(minDate)}
        endMonth={momentToTzDate(maxDate)}
        disabled={isDayDisabled}
        captionLayout="dropdown"
        timeZone={timeZone}
        locale={getConfiguredLocale(site.culture.uiLocale)}
        selected={{
          from: rangeValueTz,
          to: valueTz,
        }}
        onSelect={onSelect}
        month={monthInCalendar}
        onMonthChange={m => setMonthInCaledar(new TZDate(m, timeZone))}
        fixedWeeks
        showOutsideDays
        formatters={{
          formatWeekdayName: (date) => serverReference.abbreviatedDayLetters[date.getDay()]
        }}
        mode="range"
      />}
      <div className="flex justify-end">
        <ButtonText
          style={{
            padding: 0,
          }}
          onClick={() => setMonthInCaledar(now())}
          label={RESX.DateTime.Today}
        />
      </div>
    </div>
  </TooltipBase>
}

interface DatePickerInputProps {
  updateOnInputChange: boolean;
  focusOnMount: boolean;
  value: moment.Moment;
  error: boolean;
  minDate: moment.Moment;
  maxDate: moment.Moment;
  innerRef: React.LegacyRef<Input>;
  onChange: (value: moment.Moment) => void;
  onClick: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

const getDateString = (date: moment.Moment): string => {
  return date.format("L");
}

const DatePickerInput = (props: DatePickerInputProps) => {
  const [dateValue, setDateValue] = useState<string>(getDateString(props.value));
  const [isFocused, setIsFocused] = useState<boolean>(false);

  useEffect(() => {
    // Update date value on focus
    if (isFocused) {
      setDateValue(getDateString(props.value));
    }
  }, [isFocused]);

  useEffect(() => {
    if (!isFocused) {
      setDateValue(getDateString(props.value));
    }
  }, [props.value, isFocused]);

  const onChange = (value: string) => {
    setDateValue(value);

    if (props.updateOnInputChange) {
      updateValue(value);
    }
  }

  const updateValue = (value: string) => {
    const date = moment.utc(value, "L", moment.locale());
    if (!date.isValid()) {
      return;
    }

    props.onChange(date);
  }

  const onFocus = () => {
    setIsFocused(true);
  }

  const onBlur = () => {
    setIsFocused(false);
    updateValue(dateValue);
  }

  return <Input
    ref={props.innerRef}
    error={props.error}
    style={{ width: 120 }}
    icon="calendar"
    value={dateValue}
    onClick={props.onClick}
    onKeyDown={props.onKeyDown}
    onChange={(_, v) => onChange(v.value)}
    onBlur={onBlur}
    onFocus={onFocus}
  />
}

export const timeZone = "UTC";

export const now = (): TZDate => momentToTzDate(moment.utc());

export const momentToTzDate = (m: moment.Moment): TZDate => new TZDate(
  m.year(),
  m.month(),
  m.date(),
  timeZone
);